import axios from '../../../../config/axios';
import { ExistingAttachment } from '../../../../components/AttachmentManager';

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/product-final`;
export const PRODUCT_URL = `${API_BASE_URL}/product`;
export const PRODUCT_UPLOAD_URL = `${API_BASE_URL}/file-upload`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const normalizeProductAttachment = (
  attachment: any,
  fallbackIndex: number,
): ExistingAttachment => {
  const fileUrl = attachment?.fileUrl || attachment?.filePath || '';
  const originalName =
    attachment?.originalName ||
    attachment?.fileName ||
    fileUrl.split('/').pop() ||
    `anexo-${fallbackIndex}`;

  return {
    id: Number(attachment?.id ?? fallbackIndex),
    originalName,
    fileName: attachment?.fileName || originalName,
    filePath: attachment?.filePath || fileUrl,
    fileUrl,
    fileSize: Number(attachment?.fileSize ?? 0),
    mimeType: attachment?.mimeType || '',
    isImage: attachment?.isImage === true ? true : undefined,
    isCover: Boolean(attachment?.isCover),
  };
};

export const uploadProductFiles = async (
  files: File[],
  productId?: string,
): Promise<string[]> => {
  if (!files.length) return [];

  const headers = authHeaders();
  const uploadUrl = productId
    ? `${PRODUCT_UPLOAD_URL}?productId=${productId}`
    : PRODUCT_UPLOAD_URL;

  const uploadedUrls = await Promise.all(
    files.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(uploadUrl, formData, { headers });
      const fileUrl =
        response.data?.url ||
        response.data?.data?.url ||
        response.data?.generalFile?.fileUrl;
      if (!fileUrl) {
        throw new Error('URL do arquivo não retornada no upload');
      }
      return fileUrl;
    }),
  );

  return uploadedUrls;
};

export const fetchProductAttachmentsList = async (
  productId: string,
): Promise<ExistingAttachment[]> => {
  const response = await axios.get(`${PRODUCT_URL}/${productId}/attachments`, {
    headers: authHeaders(),
  });
  const data = Array.isArray(response.data?.data) ? response.data.data : [];
  return data.map((item: any, index: number) => normalizeProductAttachment(item, index + 1));
};

export const getProductAttachmentActionUrl = async (
  productId: string,
  attachmentId: number,
  action: 'view' | 'download',
): Promise<string | null> => {
  try {
    const response = await axios.get(
      `${PRODUCT_URL}/${productId}/attachments/${attachmentId}/${action}`,
      { headers: authHeaders() },
    );
    return response.data?.data?.url || response.data?.url || null;
  } catch {
    return null;
  }
};

export const deleteProductAttachment = async (
  productId: string,
  attachmentId: number,
): Promise<void> => {
  await axios.delete(`${PRODUCT_URL}/${productId}/attachments/${attachmentId}`, {
    headers: authHeaders(),
  });
};

export const setProductAttachmentCover = async (
  productId: string,
  attachmentId: number,
): Promise<void> => {
  await axios.patch(
    `${PRODUCT_URL}/${productId}/attachments/${attachmentId}/cover`,
    {},
    { headers: authHeaders() },
  );
};

export const removeProductAttachmentCover = async (productId: string): Promise<void> => {
  await axios.delete(`${PRODUCT_URL}/${productId}/attachments/cover`, {
    headers: authHeaders(),
  });
};

const pickPersistableAttachmentUrl = (attachment: ExistingAttachment): string | null => {
  if (attachment.fileUrl) {
    return attachment.fileUrl;
  }
  if (!attachment.filePath) {
    return null;
  }
  if (
    attachment.filePath.includes('X-Goog-') ||
    attachment.filePath.includes('GoogleAccessId=')
  ) {
    return null;
  }
  return attachment.filePath.split('?')[0] || null;
};

export const mergeAttachmentUrls = (
  _persistedUrls: string[],
  existing: ExistingAttachment[],
): string[] => {
  return Array.from(
    new Set(
      existing
        .map(pickPersistableAttachmentUrl)
        .filter((url): url is string => Boolean(url)),
    ),
  );
};

export const prepareProductAttachmentsForSubmit = async (
  selectedFiles: File[],
  existingUrls: string[],
  productId?: string,
): Promise<string[]> => {
  let urls = [...existingUrls];
  if (selectedFiles.length > 0) {
    const uploaded = await uploadProductFiles(selectedFiles, productId);
    urls = [...urls, ...uploaded];
  }
  return Array.from(new Set(urls.filter(Boolean)));
};

export type AttachmentSaveMeta = {
  pendingFiles: File[];
  baseUrls: string[];
  coverGeneralFileId?: number;
  shouldPersistAttachments: boolean;
  shouldPersistCover?: boolean;
};

export const buildAttachmentFieldsForSave = async (
  meta: AttachmentSaveMeta,
  productId?: string,
): Promise<{ attachments?: string[]; coverGeneralFileId?: number }> => {
  const result: { attachments?: string[]; coverGeneralFileId?: number } = {};

  if (meta.coverGeneralFileId !== undefined) {
    result.coverGeneralFileId = meta.coverGeneralFileId;
  }

  if (!meta.shouldPersistAttachments) {
    return result;
  }

  result.attachments = await prepareProductAttachmentsForSubmit(
    meta.pendingFiles,
    meta.baseUrls,
    productId,
  );

  return result;
};

export const attachmentsFromProductResponse = (product: any): {
  existing: ExistingAttachment[];
  urls: string[];
  coverId: number | null;
} => {
  if (Array.isArray(product?.attachmentFiles) && product.attachmentFiles.length > 0) {
    const existing = product.attachmentFiles.map((item: any, index: number) =>
      normalizeProductAttachment(item, index + 1),
    );
    const urls = existing
      .map(pickPersistableAttachmentUrl)
      .filter((url: string | null): url is string => Boolean(url));
    return {
      existing,
      urls: Array.from(new Set(urls)),
      coverId: product.coverAttachmentId ?? null,
    };
  }

  const rawUrls: string[] = Array.isArray(product?.attachments) ? product.attachments : [];
  const existing = rawUrls.map((url, index) =>
    normalizeProductAttachment(
      {
        id: 100000 + index,
        fileUrl: url,
        filePath: url,
        originalName: url.split('/').pop() || `anexo-${index + 1}`,
      },
      index + 1,
    ),
  );

  return {
    existing,
    urls: rawUrls,
    coverId: product.coverAttachmentId ?? null,
  };
};
